import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const STEPS = ['Contact', 'Delivery', 'FFL Info', 'Payment', 'Confirm'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function loadSquareScript(environment) {
  const src = environment === 'production' ? 'https://web.squarecdn.com/v1/square.js' : 'https://sandbox.web.squarecdn.com/v1/square.js';
  if (window.Square) return Promise.resolve();
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) return new Promise(resolve => existing.addEventListener('load', resolve));
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function CheckoutPage() {
  const { items, clearCart, hasFirearms: cartHasFirearms } = useCart();
  const { customer } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderResult, setOrderResult] = useState(null);

  const [contact, setContact] = useState({ email: customer?.email || '', first_name: customer?.first_name || '', last_name: customer?.last_name || '', phone: '' });
  const [shipping, setShipping] = useState({ address1: '', address2: '', city: '', state: 'LA', zip: '' });
  const [ffl, setFfl] = useState({ dealer_name: '', dealer_address: '', dealer_phone: '', license_number: '' });
  const [fulfillment, setFulfillment] = useState('ship');
  // Server-side quotes for both options, so the Delivery step can show each price
  const [quotes, setQuotes] = useState({ ship: null, pickup: null });
  const [squareReady, setSquareReady] = useState(false);
  const cardRef = useRef(null); // holds the attached Square card instance

  useEffect(() => {
    if (items.length === 0 && !orderResult) navigate('/cart');
  }, [items, navigate, orderResult]);

  const cartKey = items.map(i => i.uuid).join(',');
  useEffect(() => {
    if (!items.length) return;
    const body = (f) => ({ items: items.map(i => ({ uuid: i.uuid, title: i.title, quantity: 1 })), fulfillment: f });
    const get = (f) => axios.post('/api/orders/quote', body(f)).then(r => r.data).catch(e => ({ error: e.response?.data?.error || 'Could not price your order', pickup_only: e.response?.data?.pickup_only || [] }));
    Promise.all([get('ship'), get('pickup')]).then(([ship, pickup]) => {
      setQuotes({ ship, pickup });
      if (ship.error && !pickup.error) setFulfillment('pickup');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey]);

  const quote = quotes[fulfillment];
  const priced = quote && !quote.error;
  const pickup = fulfillment === 'pickup';
  const hasFirearms = quote?.has_firearm ?? cartHasFirearms;
  const total = priced ? quote.total : 0;

  const setC = (k, v) => setContact(f => ({ ...f, [k]: v }));
  const setS = (k, v) => setShipping(f => ({ ...f, [k]: v }));
  const setF = (k, v) => setFfl(f => ({ ...f, [k]: v }));

  // Picked-up firearms transfer in the store, so the buyer's FFL is only needed when shipping
  const steps = hasFirearms && !pickup ? STEPS : STEPS.filter(s => s !== 'FFL Info');
  const currentStep = steps[step];

  // Set up the Square card element once the Payment step is reached
  useEffect(() => {
    if (currentStep !== 'Payment' || cardRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: squareConfig } = await axios.get('/api/settings/square-config');
        if (!squareConfig.applicationId || !squareConfig.locationId) { setError('Card payments are not configured yet.'); return; }
        await loadSquareScript(squareConfig.environment);
        if (cancelled) return;
        const payments = window.Square.payments(squareConfig.applicationId, squareConfig.locationId);
        const card = await payments.card();
        await card.attach('#card-container');
        if (cancelled) { card.destroy(); return; }
        cardRef.current = card;
        setSquareReady(true);
      } catch (e) {
        setError('Unable to load the card payment form. Please refresh and try again.');
      }
    })();
    return () => {
      cancelled = true;
      if (cardRef.current) { cardRef.current.destroy(); cardRef.current = null; setSquareReady(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const next = () => { setError(''); setStep(s => s + 1); };
  const back = () => { setError(''); setStep(s => s - 1); };

  const validateContact = () => {
    if (!contact.email || !contact.first_name || !contact.last_name) { setError('Name and email are required'); return false; }
    if (!/\S+@\S+\.\S+/.test(contact.email)) { setError('Enter a valid email address'); return false; }
    return true;
  };

  const validateDelivery = () => {
    if (!priced) { setError(quote?.error || 'Still calculating your total — one moment.'); return false; }
    if (pickup) return true;
    if (!shipping.address1 || !shipping.city || !shipping.state || !shipping.zip) { setError('Complete shipping address is required'); return false; }
    return true;
  };

  const validateFfl = () => {
    if (hasFirearms && (!ffl.dealer_name || !ffl.license_number)) { setError('FFL dealer name and license number are required for firearm purchases'); return false; }
    return true;
  };

  const placeOrder = async () => {
    setLoading(true); setError('');
    try {
      if (!cardRef.current) { setError('Card form is still loading — please wait a moment and try again.'); setLoading(false); return; }
      const result = await cardRef.current.tokenize();
      if (result.status !== 'OK') {
        setError(result.errors?.[0]?.message || 'Card details are invalid.');
        setLoading(false);
        return;
      }
      const sourceId = result.token;

      const r = await axios.post('/api/orders', {
        items: items.map(i => ({ uuid: i.uuid, title: i.title, quantity: 1 })),
        fulfillment,
        guest_email: contact.email,
        guest_name: `${contact.first_name} ${contact.last_name}`,
        ship_name: `${contact.first_name} ${contact.last_name}`,
        ship_address1: shipping.address1,
        ship_address2: shipping.address2,
        ship_city: shipping.city,
        ship_state: shipping.state,
        ship_zip: shipping.zip,
        payment_method: 'card',
        source_id: sourceId,
        ffl_dealer_name: ffl.dealer_name || null,
        ffl_dealer_address: ffl.dealer_address || null,
        ffl_dealer_phone: ffl.dealer_phone || null,
        ffl_license_number: ffl.license_number || null,
      });
      clearCart();
      setOrderResult(r.data);
      setStep(steps.length - 1);
    } catch (e) {
      setError(e.response?.data?.error || 'Order failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="page-hero" style={{ padding: '28px 0' }}>
        <h1>Checkout</h1>
      </div>

      {/* Progress bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '16px 0' }}>
        <div className="container">
          <div style={{ display: 'flex', gap: 0, justifyContent: 'center' }}>
            {steps.map((s, i) => (
              <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, background: i < step ? '#c9a227' : i === step ? '#1a2744' : '#e8e8e8', color: i <= step ? '#fff' : '#9a9a9a', flexShrink: 0 }}>
                    {i < step ? '✓' : i + 1}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: i === step ? 700 : 400, color: i === step ? '#1a2744' : i < step ? '#c9a227' : '#9a9a9a', display: step === i || window.innerWidth > 600 ? 'block' : 'none' }}>{s}</span>
                </div>
                {i < steps.length - 1 && <div style={{ width: 32, height: 2, background: i < step ? '#c9a227' : '#e8e8e8', margin: '0 8px', flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '36px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 32, alignItems: 'start' }}>
          <div>
            {error && <div className="alert alert-error">{error}</div>}

            {/* Step: Contact */}
            {currentStep === 'Contact' && (
              <div>
                <h2 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 4 }}>Contact Information</h2>
                <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>Order confirmation will be sent to this email.</p>
                <div className="grid-2" style={{ gap: 16 }}>
                  <div className="form-group"><label className="form-label">First Name *</label><input className="form-control" value={contact.first_name} onChange={e => setC('first_name', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Last Name *</label><input className="form-control" value={contact.last_name} onChange={e => setC('last_name', e.target.value)} /></div>
                </div>
                <div className="form-group"><label className="form-label">Email Address *</label><input className="form-control" type="email" value={contact.email} onChange={e => setC('email', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={contact.phone} onChange={e => setC('phone', e.target.value)} placeholder="(318) 000-0000" /></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <Link to="/login" style={{ color: '#c9a227', fontSize: 13, fontWeight: 600 }}>Have an account? Sign in</Link>
                  <button className="btn btn-navy" onClick={() => { if (validateContact()) next(); }}>Continue →</button>
                </div>
              </div>
            )}

            {/* Step: Delivery */}
            {currentStep === 'Delivery' && (
              <div>
                <h2 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 16 }}>Delivery</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  {[['ship', '🚚 Ship to me'], ['pickup', '🏪 In-store pickup']].map(([val, label]) => {
                    const q = quotes[val];
                    const unavailable = q?.error;
                    return (
                      <button key={val} type="button" disabled={!q || !!unavailable} onClick={() => setFulfillment(val)}
                        style={{ textAlign: 'left', padding: '14px 16px', borderRadius: 10, cursor: unavailable ? 'not-allowed' : 'pointer', border: fulfillment === val ? '2px solid #1a2744' : '2px solid #e8e8e8', background: fulfillment === val ? 'rgba(26,39,68,0.04)' : '#fff', opacity: unavailable ? 0.5 : 1 }}>
                        <div style={{ fontWeight: 700, color: '#1a2744', fontSize: 15 }}>{label}</div>
                        <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                          {!q ? 'Calculating…' : unavailable ? 'Not available for this order' : val === 'pickup' ? 'Free' : `$${q.shipping.toFixed(2)}`}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {quotes.ship?.error && quotes.ship.pickup_only?.length > 0 && (
                  <div className="alert alert-info" style={{ marginBottom: 16 }}>🏪 {quotes.ship.pickup_only.join(', ')} {quotes.ship.pickup_only.length > 1 ? 'are' : 'is'} in-store pickup only.</div>
                )}
                {pickup ? (
                  <div className="alert alert-info" style={{ marginBottom: 16 }}>
                    We'll email you when your order is ready to pick up. Bring a photo ID.
                    {hasFirearms && ' Firearm pickups include the ATF Form 4473 and background check at our counter.'}
                  </div>
                ) : (
                  <>
                    {hasFirearms && <div className="alert alert-info" style={{ marginBottom: 16 }}>🔒 Firearms will be shipped to your FFL dealer, not this address. This address is for non-firearm items only.</div>}
                    <div className="form-group"><label className="form-label">Street Address *</label><input className="form-control" value={shipping.address1} onChange={e => setS('address1', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Apt / Suite</label><input className="form-control" value={shipping.address2} onChange={e => setS('address2', e.target.value)} /></div>
                    <div className="grid-2" style={{ gap: 16 }}>
                      <div className="form-group"><label className="form-label">City *</label><input className="form-control" value={shipping.city} onChange={e => setS('city', e.target.value)} /></div>
                      <div className="form-group"><label className="form-label">State *</label>
                        <select className="form-control" value={shipping.state} onChange={e => setS('state', e.target.value)}>
                          {US_STATES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="form-group" style={{ maxWidth: 200 }}><label className="form-label">ZIP Code *</label><input className="form-control" value={shipping.zip} onChange={e => setS('zip', e.target.value)} /></div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <button className="btn btn-ghost" onClick={back}>← Back</button>
                  <button className="btn btn-navy" onClick={() => { if (validateDelivery()) next(); }}>Continue →</button>
                </div>
              </div>
            )}

            {/* Step: FFL Info */}
            {currentStep === 'FFL Info' && (
              <div>
                <h2 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 4 }}>FFL Dealer Information</h2>
                <div className="alert alert-info" style={{ marginBottom: 20 }}>
                  <strong>Required for firearm purchase.</strong> Federal law requires all firearms to transfer through a licensed FFL dealer in your state. Provide your local dealer's info below — we'll ship directly to them. You'll complete your ATF Form 4473 and background check at pickup.
                </div>
                <div className="form-group"><label className="form-label">FFL Dealer Name *</label><input className="form-control" value={ffl.dealer_name} onChange={e => setF('dealer_name', e.target.value)} placeholder="ABC Guns & Ammo" /></div>
                <div className="form-group"><label className="form-label">Dealer Address</label><input className="form-control" value={ffl.dealer_address} onChange={e => setF('dealer_address', e.target.value)} placeholder="123 Main St, City, State ZIP" /></div>
                <div className="form-group"><label className="form-label">Dealer Phone</label><input className="form-control" value={ffl.dealer_phone} onChange={e => setF('dealer_phone', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">FFL License Number *</label><input className="form-control" value={ffl.license_number} onChange={e => setF('license_number', e.target.value)} placeholder="X-XX-XXX-XX-XX-XXXXX" /></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <button className="btn btn-ghost" onClick={back}>← Back</button>
                  <button className="btn btn-navy" onClick={() => { if (validateFfl()) next(); }}>Continue →</button>
                </div>
              </div>
            )}

            {/* Step: Payment */}
            {currentStep === 'Payment' && (
              <div>
                <h2 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 4 }}>Payment</h2>
                <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>Your payment information is secured with SSL encryption.</p>
                <div className="form-group">
                  <label className="form-label">💳 Card Details *</label>
                  <div id="card-container" style={{ minHeight: 90, padding: squareReady ? 0 : '14px', border: squareReady ? 'none' : '1px solid #e8e8e8', borderRadius: 8 }}>
                    {!squareReady && <span style={{ color: '#9a9a9a', fontSize: 13 }}>Loading secure card form…</span>}
                  </div>
                </div>
                <div style={{ background: '#f8f7f2', borderRadius: 10, padding: '14px 16px', marginTop: 16, fontSize: 13, color: '#666', display: 'flex', gap: 8 }}>
                  <span>🔒</span><span>Your payment data is encrypted. We never store your full card number.</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                  <button className="btn btn-ghost" onClick={back}>← Back</button>
                  <button className="btn btn-primary btn-lg" onClick={placeOrder} disabled={loading || !squareReady || !priced}>
                    {loading ? 'Processing...' : `Place Order · $${total.toFixed(2)}`}
                  </button>
                </div>
              </div>
            )}

            {/* Step: Confirm */}
            {currentStep === 'Confirm' && orderResult && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ width: 72, height: 72, background: 'rgba(46,125,50,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, margin: '0 auto 20px' }}>✅</div>
                <h2 style={{ fontWeight: 900, color: '#1a2744', marginBottom: 8 }}>Order Confirmed!</h2>
                <p style={{ color: '#666', fontSize: 16, marginBottom: 6 }}>Your order number is:</p>
                <div style={{ fontSize: 28, fontWeight: 900, color: '#c9a227', marginBottom: 20, fontFamily: 'monospace' }}>{orderResult.order_number}</div>
                <p style={{ color: '#555', fontSize: 14, marginBottom: 28, maxWidth: 440, margin: '0 auto 28px', lineHeight: 1.7 }}>
                  A confirmation email has been sent to <strong>{contact.email}</strong>. {pickup ? "We'll email you when it's ready to pick up — bring a photo ID." : "We'll notify you when your order ships."}
                  {hasFirearms && !pickup && <span> Your firearm will be shipped to <strong>{ffl.dealer_name}</strong> — they will contact you when it arrives.</span>}
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  <Link to="/shop" className="btn btn-navy">Continue Shopping</Link>
                  <Link to={`/order/${orderResult.order_number}?email=${encodeURIComponent(contact.email)}`} className="btn btn-outline">View Order Status</Link>
                </div>
              </div>
            )}
          </div>

          {/* Order summary sidebar */}
          {currentStep !== 'Confirm' && (
            <div style={{ position: 'sticky', top: 90 }}>
              <div className="card" style={{ padding: 20 }}>
                <h4 style={{ fontWeight: 700, color: '#1a2744', marginBottom: 16 }}>Order Summary</h4>
                {items.map(item => (
                  <div key={item.uuid} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 36, borderRadius: 6, overflow: 'hidden', background: '#f4f4f4', flexShrink: 0 }}>
                      <img src={item.primary_image || 'https://via.placeholder.com/44'} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <div style={{ flex: 1, fontSize: 13, overflow: 'hidden' }}>
                      <div style={{ fontWeight: 600, color: '#1a2744', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14, flexShrink: 0 }}>${item.price?.toFixed(2)}</div>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid #e8e8e8', paddingTop: 12, marginTop: 8 }}>
                  {[['Subtotal', priced ? `$${quote.subtotal.toFixed(2)}` : '…'], [pickup ? 'In-store pickup' : 'Shipping', priced ? (pickup ? 'Free' : `$${quote.shipping.toFixed(2)}`) : '…'], [`Tax${priced ? ` (${quote.tax_rate}%)` : ''}`, priced ? `$${quote.tax.toFixed(2)}` : '…']].map(([l, v]) => (
                    <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6, color: '#555' }}><span>{l}</span><span>{v}</span></div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 16, color: '#1a2744', borderTop: '2px solid #e8e8e8', paddingTop: 10, marginTop: 6 }}>
                    <span>Total</span><span>{priced ? `$${total.toFixed(2)}` : '…'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
